import React, {
  createContext,
  useState,
  ReactNode,
  useContext,
  useCallback,
} from "react";
import { TimesheetCardData, TimesheetEntry } from "../types/Timesheet";

interface TimesheetContextType {
  timesheetData: Record<string, TimesheetCardData>;
  setTimesheetData: React.Dispatch<
    React.SetStateAction<Record<string, TimesheetCardData>>
  >;
  updateTimesheetCardEntriesByDate: (
    date: string,
    entries: TimesheetEntry[]
  ) => void;
}

const TimesheetContext = createContext<TimesheetContextType | undefined>(
  undefined
);

const TimesheetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timesheetData, setTimesheetData] = useState<
    Record<string, TimesheetCardData>
  >({});

  const updateTimesheetCardEntriesByDate = useCallback(
    (date: string, entries: TimesheetEntry[]) => {
      setTimesheetData((prevData) => ({
        ...prevData,
        [date]: {
          ...prevData[date],
          entries,
        },
      }));
    },
    [setTimesheetData]
  );

  const contextValue = React.useMemo(
    () => ({
      timesheetData,
      setTimesheetData,
      updateTimesheetCardEntriesByDate,
    }),
    [timesheetData, updateTimesheetCardEntriesByDate]
  );

  return (
    <TimesheetContext.Provider value={contextValue}>
      {children}
    </TimesheetContext.Provider>
  );
};

// Custom hook to use TimesheetContext
const useTimesheetData = () => {
  const context = useContext(TimesheetContext);
  if (!context) {
    throw new Error("useTimesheetData must be used within a TimesheetProvider");
  }
  return context;
};

export { TimesheetContext, TimesheetProvider, useTimesheetData };
